<template>
  <form @submit.prevent="onSubmit">
    <!-- ------------ -->
    <!--   DIALOGS    -->
    <!-- ------------ -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ----------------- -->
    <!-- TOP ROW (HEADER)  -->
    <!-- ----------------- -->
    <div class="row h-40">
      <p class="bb-page-title mt-3 mb-1">SPELL DETAILS</p>
    </div>
    <!-- ------------------------------------------------------------>
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row mb-2 mt-0"
    >
      <!-- ------------------------------------------------------------>
      <!--    LEFT COL (BUTTONS)   -->
      <!-- ----------------------- -->
      <div class="col-3">
        <!-- ---------- -->
        <!-- BUTTONS    -->
        <!-- ---------- -->
        <div class="row mt-4">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="manageCancel()"
            >
              Cancel
            </button>
            <!-- ------------ -->
            <!--    delete    -->
            <!-- ------------ -->
            <button
              v-if="!isNewSpell && !this.delete"
              class="btn btn-primary btn-lg"
              type="button"
              @click="confirmDeletion()"
            >
              Delete
            </button>
            <!-- ------------ -->
            <!--     add      -->
            <!-- ------------ -->
            <button
              v-if="isNewSpell && hasSpellName"
              :disabled="isError"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <!-- ------------ -->
            <!--      ok      -->
            <!-- ------------ -->
            <button
              v-if="!isNewSpell"
              :disabled="!isChanged || isError"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              OK
            </button>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!--    RIGHT COL (DATA)   -->
      <!-- --------------------- -->
      <div class="col m-3 bb-tooltip-wrapper">
        <!-- ---------- -->
        <!--    type    -->
        <!-- ---------- -->
        <p class="bb-hdg-lg">{{ spellHeading }}</p>
        <!-- ======================================================================== -->
        <!--   NEW SPELL    -->
        <!-- ======================================================================== -->

        <div
          v-if="isNewSpell"
          class="row"
        >
          <!-- ======================================================== -->
          <!--  A. spell type passed in = ALL                           -->
          <!-- ======================================================== -->
          <template v-if="spell_type === 'All'">
            <!-- ====================================================== -->
            <!-- A1. selected spell type = RUNE                         -->
            <!-- ROW 1: [type] [rune selection]                         -->
            <!-- ROW 2: [spell selection] and, if progressive, [mag]    -->
            <!-- ====================================================== -->
            <template v-if="activeSpell.type === 'Rune'">
              <!-- ROW 1 -->
              <div class="row mt-2 mb-2">
                <!-- spell type (input) -->
                <div class="col-4">
                  <div class="form-group">
                    <div class="form-floating">
                      <v-select
                        v-model="local_spell.type"
                        :options="spell_types"
                        class="form-control"
                        placeholder="Select..."
                        :class="{ 'is-invalid': errorFields.includes('type') }"
                        @keydown="onKeyDown($event)"
                        @update:modelValue="onSpellTypeSelect"
                        id="spell_type"
                        @click="clearInputError('type')"
                      >
                        <template #search="{ attributes, events }">
                          <input
                            class="vs__search"
                            :required="!local_spell.type"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>
                      <label
                        class="bb-float-label"
                        for="spell_type"
                        >Spell Type</label
                      >
                    </div>
                  </div>
                </div>

                <!-- rune selection (input) -->
                <div class="col-8">
                  <div class="form-group">
                    <div class="form-floating">
                      <v-select
                        :options="runes"
                        :get-option-label="getRuneLabel"
                        :reduce="reduceObject"
                        class="form-control"
                        placeholder="Select..."
                        v-model="local_spell.character_rune"
                        @keydown="onKeyDown($event)"
                        :class="{
                          'is-invalid': errorFields.includes('character_rune'),
                        }"
                        @click="clearInputError('character_rune')"
                        @update:modelValue="onRuneSelect"
                        id="character_rune"
                      >
                        <template #search="{ attributes, events }">
                          <input
                            class="vs__search"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>
                      <label
                        class="bb-float-label"
                        for="character_rune"
                        >Active Casting Rune</label
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- linked item (r/o) in its own row for clearer layout -->
              <div
                v-if="local_spell.character_rune && local_spell.item_name"
                class="row mt-2 mb-2"
              >
                <div class="col-7">
                  <div class="form-group">
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control"
                        :value="
                          local_spell.item_name || local_spell.source_name
                        "
                        id="linked_item"
                        disabled
                      />
                      <label
                        class="bb-float-label optional"
                        for="linked_item"
                        >Linked Item</label
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- ROW 2: only once a rune is selected -->
              <div
                v-if="local_spell.character_rune"
                class="row mt-2 mb-2"
              >
                <!-- spell name (input) -->
                <div
                  :class="[
                    'form-group',
                    hasSpellName && local_spell.progressive
                      ? 'col-7'
                      : 'col-12',
                  ]"
                >
                  <div class="form-floating">
                    <v-select
                      :options="spells"
                      :get-option-label="getSpellLabel"
                      :reduce="reduceObject"
                      class="form-control"
                      placeholder="Select..."
                      v-model="local_spell.spell"
                      @keydown="onKeyDown($event)"
                      :class="{ 'is-invalid': errorFields.includes('spell') }"
                      @click="clearInputError('spell')"
                      @update:modelValue="onSpellSelect"
                      id="spell"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <label
                      class="bb-float-label"
                      for="spell"
                      >Spell</label
                    >
                  </div>
                </div>

                <!-- mag (counter) -->
                <div
                  v-if="hasSpellName && local_spell.progressive"
                  class="col-5"
                >
                  <CounterEmit
                    ref="magCounter"
                    :pass_ref="'mag'"
                    :pass_label="'Mag'"
                    :pass_count="local_spell.mag"
                    :pass_min="computeMagMin"
                    :pass_max="10"
                    :pass_digits="2"
                    :invalid="errorFields.includes('mag')"
                    @change="onCounterChanged"
                  />
                </div>
              </div>
            </template>

            <!-- ======================================================= -->
            <!-- A2. selected spell type = DIVINE / SORCERY              -->
            <!-- ROW 1: [type] [spell selection]                         -->
            <!-- ROW 2: [item selection]                                 -->
            <!-- ======================================================= -->
            <template
              v-else-if="
                activeSpell.type === 'Sorcery' || activeSpell.type === 'Divine'
              "
            >
              <!-- ROW 1 -->
              <div class="row mt-2 mb-2">
                <!-- spell type (input) -->
                <div class="col-4">
                  <div class="form-group">
                    <div class="form-floating">
                      <v-select
                        v-model="local_spell.type"
                        :options="spell_types"
                        class="form-control"
                        placeholder="Select..."
                        :class="{ 'is-invalid': errorFields.includes('type') }"
                        @keydown="onKeyDown($event)"
                        @update:modelValue="onSpellTypeSelect"
                        id="spell_type"
                        @click="clearInputError('type')"
                      >
                        <template #search="{ attributes, events }">
                          <input
                            class="vs__search"
                            :required="!local_spell.type"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>
                      <label
                        class="bb-float-label"
                        for="spell_type"
                        >Spell Type</label
                      >
                    </div>
                  </div>
                </div>

                <!-- spell name (input) -->
                <div class="form-group col-8">
                  <div class="form-floating">
                    <v-select
                      :options="spells"
                      :get-option-label="getSpellLabel"
                      :reduce="reduceObject"
                      class="form-control"
                      placeholder="select..."
                      v-model="local_spell.spell"
                      @keydown="onKeyDown($event)"
                      :class="{ 'is-invalid': errorFields.includes('spell') }"
                      @click="clearInputError('spell')"
                      @update:modelValue="onSpellSelect"
                      id="spell"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <label
                      class="bb-float-label"
                      for="spell"
                      >Spell</label
                    >
                  </div>
                </div>
              </div>

              <!-- ROW 2: item selection -->
              <div
                v-if="local_spell.spell"
                class="row mt-2 mb-2"
              >
                <div class="form-group col-8">
                  <div class="form-floating">
                    <v-select
                      :options="items"
                      :get-option-label="getItemLabel"
                      :reduce="reduceObject"
                      class="form-control"
                      placeholder="optional select..."
                      v-model="local_spell.character_item"
                      @keydown="onKeyDown($event)"
                      :class="{ 'is-invalid': errorFields.includes('item') }"
                      @click="clearInputError('item')"
                      @update:modelValue="onItemSelect"
                      id="item"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <label
                      class="bb-float-label"
                      for="item"
                      >Item</label
                    >
                  </div>
                </div>
              </div>

              <!-- progressive mag (separate row, if applicable) -->
              <div
                v-if="hasSpellName && local_spell.progressive"
                class="row mt-2 mb-3"
              >
                <div class="col-4">
                  <CounterEmit
                    ref="magCounter"
                    :pass_ref="'mag'"
                    :pass_label="'Mag'"
                    :pass_count="local_spell.mag"
                    :pass_min="computeMagMin"
                    :pass_max="10"
                    :pass_digits="2"
                    :invalid="errorFields.includes('mag')"
                    @change="onCounterChanged"
                  />
                </div>
              </div>
            </template>

            <!-- Fallback while no spell type selected yet -->
            <template v-else>
              <div class="row mt-2 mb-2">
                <div class="col-4">
                  <div class="form-group">
                    <div class="form-floating">
                      <v-select
                        v-model="local_spell.type"
                        :options="spell_types"
                        class="form-control"
                        placeholder="Select..."
                        :class="{ 'is-invalid': errorFields.includes('type') }"
                        @keydown="onKeyDown($event)"
                        @update:modelValue="onSpellTypeSelect"
                        id="spell_type"
                        @click="clearInputError('type')"
                      >
                        <template #search="{ attributes, events }">
                          <input
                            class="vs__search"
                            :required="!local_spell.type"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>
                      <label
                        class="bb-float-label"
                        for="spell_type"
                        >Spell Type</label
                      >
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <!-- ======================================================== -->
          <!--  B. spell type passed in = RUNE                          -->
          <!--      ROW 1: [rune selection]                             -->
          <!--      ROW 1 (after select): [rune] [spell] [linked item]  -->
          <!--      ROW 2: [mag] (if progressive)                       -->
          <!-- ======================================================== -->
          <template v-else-if="spell_type === 'Rune'">
            <!-- ROW 1: rune + spell (+ linked item) -->
            <div class="row mt-2 mb-2">
              <!-- rune selection -->
              <div class="col-8">
                <div class="form-group">
                  <div class="form-floating">
                    <v-select
                      :options="runes"
                      :get-option-label="getRuneLabel"
                      :reduce="reduceObject"
                      class="form-control"
                      placeholder="Select..."
                      v-model="local_spell.character_rune"
                      @keydown="onKeyDown($event)"
                      :class="{
                        'is-invalid': errorFields.includes('character_rune'),
                      }"
                      @click="clearInputError('character_rune')"
                      @update:modelValue="onRuneSelect"
                      id="character_rune"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>
                    <label
                      class="bb-float-label"
                      for="character_rune"
                      >Active Casting Rune</label
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-2">
              <!-- linked item (r/o) -->
              <div
                v-if="local_spell.character_rune && local_spell.item_name"
                class="col-8"
              >
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control"
                      :value="local_spell.item_name || local_spell.source_name"
                      id="linked_item"
                      disabled
                    />
                    <label
                      class="bb-float-label"
                      for="linked_item"
                      >Linked Item</label
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="row mb-2">
              <!-- spell selection (only once a rune is selected) -->
              <div
                v-if="local_spell.character_rune"
                :class="[
                  'form-group',
                  hasSpellName && local_spell.progressive ? 'col-8' : 'col-12',
                ]"
              >
                <div class="form-floating">
                  <v-select
                    :options="spells"
                    :get-option-label="getSpellLabel"
                    :reduce="reduceObject"
                    class="form-control"
                    placeholder="Select..."
                    v-model="local_spell.spell"
                    @keydown="onKeyDown($event)"
                    :class="{ 'is-invalid': errorFields.includes('spell') }"
                    @click="clearInputError('spell')"
                    @update:modelValue="onSpellSelect"
                    id="spell"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="spell"
                    >Spell</label
                  >
                </div>
              </div>

              <div
                v-if="hasSpellName && local_spell.progressive"
                class="col-4"
              >
                <CounterEmit
                  ref="magCounter"
                  :pass_ref="'mag'"
                  :pass_label="'Mag'"
                  :pass_count="local_spell.mag"
                  :pass_min="computeMagMin"
                  :pass_max="10"
                  :pass_digits="2"
                  :invalid="errorFields.includes('mag')"
                  @change="onCounterChanged"
                />
              </div>
            </div>
          </template>

          <!-- ======================================================== -->
          <!--  C. spell type passed in = DIVINE / SORCERY             -->
          <!--      ROW 1: [type] [spell selection]                    -->
          <!--      ROW 2: [item selection]                           -->
          <!-- ======================================================== -->
          <template
            v-else-if="spell_type === 'Sorcery' || spell_type === 'Divine'"
          >
            <!-- ROW 1: type (r/o) + spell -->
            <div class="row mt-2 mb-2">
              <!-- spell type (r/o) -->
              <div class="col-3">
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control"
                      :value="activeSpell.type || spell_type"
                      id="spell_type_fixed"
                      disabled
                    />
                    <label
                      class="bb-float-label"
                      for="spell_type_fixed"
                      >Spell Type</label
                    >
                  </div>
                </div>
              </div>

              <!-- spell name (input) -->
              <div class="form-group col-7">
                <div class="form-floating">
                  <v-select
                    :options="spells"
                    :get-option-label="getSpellLabel"
                    :reduce="reduceObject"
                    class="form-control"
                    placeholder="Select..."
                    v-model="local_spell.spell"
                    @keydown="onKeyDown($event)"
                    :class="{ 'is-invalid': errorFields.includes('spell') }"
                    @click="clearInputError('spell')"
                    @update:modelValue="onSpellSelect"
                    id="spell"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="spell"
                    >Spell</label
                  >
                </div>
              </div>
            </div>

            <!-- ROW 2: item selection -->
            <div
              v-if="local_spell.spell"
              class="row mt-2 mb-2"
            >
              <div class="form-group col-7">
                <div class="form-floating">
                  <v-select
                    :options="items"
                    :get-option-label="getItemLabel"
                    :reduce="reduceObject"
                    class="form-control"
                    placeholder="Optional select..."
                    v-model="local_spell.character_item"
                    @keydown="onKeyDown($event)"
                    :class="{ 'is-invalid': errorFields.includes('item') }"
                    @click="clearInputError('item')"
                    @update:modelValue="onItemSelect"
                    id="item"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="item"
                    >Item</label
                  >
                </div>
              </div>
            </div>

            <!-- progressive mag (separate row, if applicable) -->
            <div
              v-if="hasSpellName && local_spell.progressive"
              class="row mt-2 mb-3"
            >
              <div class="col-4">
                <CounterEmit
                  ref="magCounter"
                  :pass_ref="'mag'"
                  :pass_label="'Mag'"
                  :pass_count="local_spell.mag"
                  :pass_min="computeMagMin"
                  :pass_max="10"
                  :pass_digits="2"
                  :invalid="errorFields.includes('mag')"
                  @change="onCounterChanged"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- ======================================================================== -->
        <!--   EXISTING SPELL    -->
        <!-- ======================================================================== -->

        <!-- -------------- -->
        <!--      ROW 1     -->
        <!-- -------------- -->
        <div
          v-else
          class="row mt-2 mb-2"
        >
          <!-- --------------------- -->
          <!--   row number (r/o)    -->
          <!-- --------------------- -->
          <div class="col-2">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  v-model="rowNumber"
                  id="row"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="row"
                  >Row</label
                >
              </div>
            </div>
          </div>

          <!-- ------------------- -->
          <!--   spell type (r/o)   -->
          <!-- -------------------- -->
          <div class="col-3">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="local_spell.type"
                  id="spell_type"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="spell_type"
                  >Spell Type</label
                >
              </div>
            </div>
          </div>

          <!-- -------------------- -->
          <!--   spell name (r/o)   -->
          <!-- -------------------- -->
          <div class="col-7">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="local_spell.name"
                  id="spell_name"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="spell_name"
                  >Spell Name</label
                >
              </div>
            </div>
          </div>
        </div>

        <!-- --------------------------- -->
        <!--   casting rune name (r/o)   -->
        <!-- --------------------------- -->
        <div class="row mb-2">
          <div
            v-if="local_spell.casting_rune_name"
            class="col-5"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="local_spell.casting_rune_name"
                  id="rune_name"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="rune_name"
                  >Rune Name</label
                >
              </div>
            </div>
          </div>

          <!-- --------------------- -->
          <!--   linked item (r/o)   -->
          <!-- --------------------- -->
          <div
            v-if="local_spell.item_name || local_spell.source_name"
            class="col-7"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="local_spell.item_name || local_spell.source_name"
                  id="linked_item"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="linked_item"
                  >Linked Item</label
                >
              </div>
            </div>
          </div>
        </div>

        <!-- ======================================================================== -->
        <!--   NEW OR EXISTING SPELL  -->
        <!-- ======================================================================== -->

        <div v-if="hasSpellName">
          <!-- ------------ -->
          <!--   DETAILS    -->
          <!-- ------------ -->
          <!-- <p class="bb-sub-hdg mb-3 mt-3">Details</p> -->
          <!-- -------------- -->
          <!--      ROW 3     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ---------------------- -->
            <!--   casting time (r/o)   -->
            <!-- ---------------------- -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.casting_time"
                    id="casting_time"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="casting_time"
                    >Casting Time</label
                  >
                </div>
              </div>
            </div>
            <!-- ------------------ -->
            <!--   duration (r/o)   -->
            <!-- ------------------ -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.duration"
                    id="duration"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="duration"
                    >Duration</label
                  >
                </div>
              </div>
            </div>
            <!-- ------------- -->
            <!--   area (r/o)   -->
            <!-- -------------- -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.area"
                    id="area"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="area"
                    >Area</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------- -->
            <!--   range (r/o)   -->
            <!-- --------------- -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.range"
                    id="range"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="range"
                    >Range</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------- -->
          <!--      ROW 4     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- --------------- -->
            <!--  resist (r/o)   -->
            <!-- --------------- -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.resist"
                    id="resist"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="resist"
                    >Resist</label
                  >
                </div>
              </div>
            </div>
            <!-- -------------------- -->
            <!--  progressive (r/o)   -->
            <!-- -------------------- -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.progressive ? 'Yes' : 'No'"
                    id="progressive"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="progressive"
                    >Progressive</label
                  >
                </div>
              </div>
            </div>
            <!-- ------------------ -->
            <!--  triggered (r/o)   -->
            <!-- ------------------ -->
            <div class="col-3">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.triggered ? 'Yes' : 'No'"
                    id="triggered"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="triggered"
                    >Triggered</label
                  >
                </div>
              </div>
            </div>
            <!-- ----------- -->
            <!--  mag (r/o)   -->
            <!-- ------------ -->
            <div
              v-if="!isNewSpell || !local_spell.progressive"
              class="col-3"
            >
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_spell.mag"
                    id="mag"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="mag"
                    >Mag</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------- -->
          <!--     ROW 5      -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- -------------------------- -->
            <!--  short description (r/o)   -->
            <!-- -------------------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <textarea
                    id="short_description"
                    name="short_description"
                    type="text"
                    rows="2"
                    class="form-control bb-textarea-with-label bb-card-short-description-control"
                    v-model="local_spell.short_description"
                    disabled
                  ></textarea>
                  <label
                    class="bb-float-label optional"
                    for="short_description"
                    >Short Description</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------- -->
          <!--     ROW 6      -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- -------------------- -->
            <!--  description (r/o)   -->
            <!-- -------------------- -->
            <div class="form-group">
              <div class="form-floating">
                <textarea
                  id="description"
                  name="description"
                  type="text"
                  rows="5"
                  class="form-control bb-textarea-with-label bb-card-description-control"
                  v-model="local_spell.description"
                  disabled
                ></textarea>
                <label
                  class="bb-float-label optional"
                  for="description"
                  >Description</label
                >
              </div>
            </div>
          </div>
        </div>
        <!-- ============ -->
        <!--    ERRORS    -->
        <!-- ============ -->
        <div v-if="errors.length > 0">
          <div class="form-group">
            <div class="row">
              <div class="col text-end me-3">
                <div
                  v-for="err in errors"
                  :key="err"
                  class="bb-error-wrap"
                  style="white-space: pre-line"
                >
                  {{ err }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'
import CounterEmit from './CounterEmit.vue'

import isObjectChanged from '../helpers/isObjectChanged'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import setSpellValues from '../helpers/setSpellValues'
import createTempId from '../helpers/createTempId'
import isEmpty from '../helpers/isEmpty'
import isAlphanumeric from '../helpers/isAlphanumeric'

import { inject } from 'vue'

export default {
  name: 'SpellCard',
  // NO UPDATE EMITS
  emits: [
    'add-character-spell',
    'update-character-spell',
    'delete-character-spell',
    'close-spell-panel',
  ],
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    CounterEmit,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // props
  //-------
  props: {
    spell: {
      type: Object,
      default: null,
    },
    spell_type: {
      type: String,
      required: true,
    },
    character: {
      type: Object,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      original_spell: {
        mag: 0,
        character_item: null,
        character_rune: null,
      },
      local_spell: {},
      //
      spell_types: ['Rune', 'Sorcery', 'Divine'],
      errors: [],
      errorFields: [],
      spells: [],
      runes: [],
      items: [],
      //
      submitCommitted: false,
      //
      delete: false,
    }
  },
  //==========================================================
  // watch
  //-------
  watch: {},
  //==========================================
  // computed
  //-----------
  computed: {
    computeMagMin() {
      if (this.local_spell.progressive) return 1
      return 0
    },
    hasErrors() {
      return this.errors.length > 0
    },
    spellType() {
      if (this.spell_type === 'All') {
        return 'All Spells'
      } else {
        return this.spell_type
      }
    },
    spellHeading() {
      if (isEmpty(this.activeSpell?.name)) {
        return this.spellType
      }
      // use same formatter as dropdown so casing is consistent
      return this.formatSpellName(this.activeSpell?.name || '')
    },
    isReady() {
      return !isEmpty(this.activeSpell) && !isEmpty(this.local_spell)
    },
    activeSpell() {
      // existing or new spell
      const hasLocal =
        !isEmpty(this.local_spell) && Object.keys(this.local_spell).length > 0
      return hasLocal ? this.local_spell : (this.spell ?? {})
    },
    isNewSpell() {
      const s = this.activeSpell ?? null
      const id = s?.character_spell_id
        ? s?.character_spell_id
        : s?.character_item_spell_id
          ? s?.character_item_spell_id
          : s?.character_rune_spell_id
            ? s?.character_rune_spell_id
            : (s?.id ?? null)
      console.log('isNewSpell = ', id)
      return !this.activeSpell || id === 0 || id == null
    },
    rowNumber() {
      return (this.activeSpell?.originalIndex ?? 0) + 1
    },
    hasSpellName() {
      const name = this.activeSpell?.name || ''
      const result = name !== ''
      return result
    },
    isError() {
      return this.errors.length > 0
    },
    isChanged() {
      // new spells...
      if (this.isNewSpell) {
        return isObjectChanged(this.original_spell, this.activeSpell)
      }

      const originalComparable = {
        mag: this.original_spell.mag ?? 0,
        character_item: this.original_spell.character_item ?? null,
        character_rune: this.original_spell.character_rune ?? null,
      }

      const currentComparable = {
        mag: this.activeSpell?.mag ?? 0,
        character_item: this.activeSpell?.character_item ?? null,
        character_rune: this.activeSpell?.character_rune ?? null,
      }

      const baseChanged = isObjectChanged(originalComparable, currentComparable)

      return baseChanged
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //========================================================
    // format spell name (preserve apostrophes)
    //-----------------------------------------
    formatSpellName(name) {
      if (!name || typeof name !== 'string') return ''
      // always normalise casing so we correct names that were
      // previously transformed by CSS (e.g. "Bandit'S" -> "Bandit's")
      // while keeping characters after apostrophes lowercase.
      return name
        .split(' ')
        .map((word) => {
          if (!word) return ''
          const first = word.charAt(0).toUpperCase()
          const rest = word.slice(1).toLowerCase()
          return first + rest
        })
        .join(' ')
    },
    //========================================================
    // get dropdown label
    //--------------------
    getRuneLabel(option) {
      if (!option) return ''
      // Prefer per-instance display_name (e.g. "Darkness 2") when set,
      // otherwise fall back to the base rune name.
      return option.display_name || option.name || ''
    },
    getSpellLabel(option) {
      if (!option) return ''
      const raw = option.name || ''
      return this.formatSpellName(raw)
    },
    getItemLabel(option) {
      if (!option) return ''
      return option.display_name || option.name || ''
    },

    //========================================================
    // normalize rune identifiers for matching
    //----------------------------------------
    normalizeRuneName(name) {
      return (name ?? '').toString().trim().toLowerCase()
    },

    getCharacterRuneStaticId(characterRune) {
      if (!characterRune) return null

      return characterRune.rune ?? characterRune.rune_id ?? null
    },

    getSpellCastingRuneId(spell) {
      if (!spell) return null

      return spell.casting_rune ?? spell.casting_rune_id ?? null
    },

    getSpellSecondaryRuneId(spell) {
      if (!spell) return null

      return spell.secondary_rune ?? spell.secondary_rune_id ?? null
    },

    runeRefsMatch({ leftId, rightId, leftName, rightName }) {
      if (leftId != null && rightId != null) {
        return String(leftId) === String(rightId)
      }

      const normalizedLeft = this.normalizeRuneName(leftName)
      const normalizedRight = this.normalizeRuneName(rightName)

      return normalizedLeft !== '' && normalizedLeft === normalizedRight
    },

    getActiveCharacterRunes() {
      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []
      return runes.filter((rune) => rune?.active === true)
    },

    spellMatchesCastingRune(spell, characterRune) {
      if (!spell || !characterRune) return false

      return this.runeRefsMatch({
        leftId: this.getSpellCastingRuneId(spell),
        rightId: this.getCharacterRuneStaticId(characterRune),
        leftName: spell.casting_rune_name,
        rightName: characterRune.name,
      })
    },

    spellMatchesActiveSecondaryRune(spell) {
      if (!spell) return false

      const secondaryRuneId = this.getSpellSecondaryRuneId(spell)
      const secondaryRuneName = this.normalizeRuneName(
        spell.secondary_rune_name,
      )

      if (secondaryRuneId == null && secondaryRuneName === '') {
        return true
      }

      return this.getActiveCharacterRunes().some((characterRune) =>
        this.runeRefsMatch({
          leftId: secondaryRuneId,
          rightId: this.getCharacterRuneStaticId(characterRune),
          leftName: secondaryRuneName,
          rightName: characterRune.name,
        }),
      )
    },

    filterRuneSpellsForCharacterRune(spells, characterRune) {
      const spellOptions = Array.isArray(spells) ? spells : []

      if (!characterRune) {
        return []
      }

      return spellOptions.filter(
        (spell) =>
          this.spellMatchesCastingRune(spell, characterRune) &&
          this.spellMatchesActiveSecondaryRune(spell),
      )
    },

    //========================================================
    // reduce dropdown selected option
    //---------------------------------
    reduceObject(option) {
      if (!option) return null
      return option.id ?? null
    },

    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },

    //========================================================
    // get comparable static spell id from row
    //----------------------------------------
    getComparableSpellId(spellRow) {
      if (!spellRow) return null

      return spellRow.spell ?? spellRow.static_id ?? null
    },

    //========================================================
    // get linked rune id from spell row
    //-----------------------------------
    getComparableRuneId(spellRow) {
      if (!spellRow) return null

      return (
        spellRow.character_rune ??
        spellRow.character_rune_id ??
        spellRow.source_id ??
        null
      )
    },

    //========================================================
    // get linked rune name from spell row
    //-------------------------------------
    getComparableRuneName(spellRow) {
      if (!spellRow) return ''

      const directName =
        spellRow.casting_rune_name ??
        spellRow.rune_name ??
        spellRow.source_name ??
        ''

      if (directName) {
        return directName.toString().trim().toLowerCase()
      }

      const runeId = this.getComparableRuneId(spellRow)
      if (runeId == null || !Array.isArray(this.character?.runes)) return ''

      const matchingRune = this.character.runes.find(
        (r) => r && String(r.id) === String(runeId),
      )

      return (matchingRune?.display_name ?? matchingRune?.name ?? '')
        .toString()
        .trim()
        .toLowerCase()
    },

    //========================================================
    // determine if spell is rune-scoped
    //----------------------------------
    isRuneScopedSpell(spellRow) {
      if (!spellRow) return false

      const source = (spellRow.source ?? spellRow.type ?? this.spell_type ?? '')
        .toString()
        .trim()
        .toLowerCase()

      return (
        source === 'rune' ||
        this.getComparableRuneId(spellRow) != null ||
        this.getComparableRuneName(spellRow) !== ''
      )
    },

    //========================================================
    // compare spell identity
    //------------------------
    hasSameSpellIdentity({
      existingSpellId,
      targetSpellId,
      existingName,
      targetName,
    }) {
      if (
        existingSpellId != null &&
        targetSpellId != null &&
        String(existingSpellId) === String(targetSpellId)
      ) {
        return true
      }

      return !!targetName && existingName === targetName
    },

    //========================================================
    // compare rune source identity
    //------------------------------
    hasSameRuneSource({
      existingRuneId,
      targetRuneId,
      existingRuneName,
      targetRuneName,
    }) {
      if (existingRuneId != null && targetRuneId != null) {
        return String(existingRuneId) === String(targetRuneId)
      }

      return !!targetRuneName && existingRuneName === targetRuneName
    },

    //========================================================
    // get spell row id
    //------------------
    getSpellRowId(spellRow) {
      if (!spellRow) return null

      return (
        spellRow.id ??
        spellRow.character_spell_id ??
        spellRow.character_item_spell_id ??
        spellRow.character_rune_spell_id ??
        null
      )
    },

    //========================================================
    // revalidate duplicate spell + mag
    //----------------------------------
    revalidateDuplicateSpellMag(errorField = 'mag') {
      const spellId =
        this.save_spell_id ??
        this.getComparableSpellId(this.activeSpell) ??
        null

      if (!spellId) {
        this.clearInputError(errorField)
        return false
      }

      return this.validateDuplicateSpellMag({
        spellId,
        mag: this.activeSpell?.mag,
        excludeId: this.isNewSpell
          ? null
          : this.getSpellRowId(this.activeSpell),
        errorField,
      })
    },

    //========================================================
    // validate duplicate spell + mag
    //---------------------------------
    validateDuplicateSpellMag({ spellId, mag, excludeId = null, errorField }) {
      const existingSpells = Array.isArray(this.character?.spells)
        ? this.character.spells
        : []

      if (!spellId || existingSpells.length === 0) {
        // nothing to validate against
        return false
      }

      const newMag = mag == null ? 0 : Number(mag)
      const targetName = (this.activeSpell?.name ?? '')
        .toString()
        .trim()
        .toLowerCase()
      const runeScoped = this.isRuneScopedSpell(this.activeSpell)
      const targetRuneId = runeScoped
        ? this.getComparableRuneId(this.activeSpell)
        : null
      const targetRuneName = runeScoped
        ? this.getComparableRuneName(this.activeSpell)
        : ''

      const hasDuplicate = existingSpells.some((cs) => {
        const existingId =
          cs.id ??
          cs.character_spell_id ??
          cs.character_item_spell_id ??
          cs.character_rune_spell_id ??
          null
        const existingSpellId = this.getComparableSpellId(cs)
        const existingMagRaw = cs.mag
        const existingMag = existingMagRaw == null ? 0 : Number(existingMagRaw)
        const existingName = (cs.name ?? '').toString().trim().toLowerCase()
        const existingRuneId = this.getComparableRuneId(cs)
        const existingRuneName = this.getComparableRuneName(cs)

        if (excludeId && existingId === excludeId) return false

        const sameSpell = this.hasSameSpellIdentity({
          existingSpellId,
          targetSpellId: spellId,
          existingName,
          targetName,
        })

        if (!sameSpell || existingMag !== newMag) return false

        if (runeScoped) {
          return this.hasSameRuneSource({
            existingRuneId,
            targetRuneId,
            existingRuneName,
            targetRuneName,
          })
        }

        return true
      })

      if (hasDuplicate) {
        this.clearInputError('spell')
        this.clearInputError(errorField)
        this.errors.push(
          window.bbUiErrorMsg(`Spell already known for Mag ${newMag}`),
        )
        this.errorFields.push(errorField)
        return true
      }

      // clear any previous error for this field when no duplicate
      this.clearInputError('spell')
      this.clearInputError(errorField)
      return false
    },

    //========================================================
    // filter dropdown spells to exclude fixed-mag duplicates
    //--------------------------------------------------------
    filterAvailableStaticSpells(staticSpells) {
      console.log('=====================================')
      console.log('filterAvailableStaticSpells....')

      /*
        - "name": spell name,
        - "spell": static spell id,
        - "source": "Character", "Item", or "Rune",
        - "source_id": character_id, character_item_id, or character_rune_id (matching the source)
        - "character_spell_id": character_spell_id, character_item_spell, or character_rune_spell_id
      */

      const static_spells = Array.isArray(staticSpells) ? staticSpells : []
      const character_spells = Array.isArray(this.character?.spells)
        ? this.character.spells
        : []
      // console.log('-------------------------------------')
      // console.log('static_spells = ', static_spells)

      // console.log('-------------------------------------')
      // console.log('character_spells = ', character_spells)

      // console.log('-------------------------------------')

      // where static spell progressive:
      // a) true - include - will check on save to ensure no dupes (opportunity change mag)
      // b) false - exclude when the same fixed-mag spell already exists
      //    on this rune, or anywhere on the character for non-rune spells

      const targetName = (this.activeSpell?.name ?? '')
        .toString()
        .trim()
        .toLowerCase()
      const runeScoped = this.isRuneScopedSpell(this.activeSpell)
      const targetRuneId = runeScoped
        ? this.getComparableRuneId(this.activeSpell)
        : null
      const targetRuneName = runeScoped
        ? this.getComparableRuneName(this.activeSpell)
        : ''

      const available_spells = static_spells.filter((s) => {
        if (!s || s.id == null) return false
        if (s.progressive === true) return true

        console.log(
          '=========================================================================',
        )
        console.log(
          'checking static spell name = ',
          s.name,
          ' id = ',
          s.id,
          ' mag = ',
          s.mag,
        )
        console.log('-------------------------------------')

        // for non-progressive spells - check if already in character spells
        const existsOnCharacter = character_spells.some((cs) => {
          const cs_spell_id = this.getComparableSpellId(cs)
          const cs_progressive = cs.progressive ?? null
          const cs_mag = cs.mag ?? null
          const cs_name = (cs.name ?? '').toString().trim().toLowerCase()
          const static_name = (s.name ?? '').toString().trim().toLowerCase()
          const cs_rune_id = this.getComparableRuneId(cs)
          const cs_rune_name = this.getComparableRuneName(cs)

          console.log(
            'checking character spell name = ',
            cs.name,
            ' s.id = ',
            s.id,
            ' cs_spell_id = ',
            cs_spell_id,
            ' cs_progressive = ',
            cs_progressive,
            ' s.mag = ',
            s.mag,
            ' cs_rune_id = ',
            cs_rune_id,
          )

          const sameSpell = this.hasSameSpellIdentity({
            existingSpellId: cs_spell_id,
            targetSpellId: s.id,
            existingName: cs_name,
            targetName: static_name || targetName,
          })

          if (!sameSpell || cs_progressive !== false || cs_mag !== s.mag) {
            return false
          }

          if (runeScoped) {
            return this.hasSameRuneSource({
              existingRuneId: cs_rune_id,
              targetRuneId,
              existingRuneName: cs_rune_name,
              targetRuneName,
            })
          }

          return true
        })

        return !existsOnCharacter
      })
      console.log(
        'filterAvailableStaticSpells: available_spells = ',
        available_spells,
      )

      return available_spells
    },

    //========================================================
    // on counter changed
    //--------------------
    async onCounterChanged({ ref, count }) {
      console.log(
        'onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
        ' isNewSpell = ',
        this.isNewSpell,
      )
      //--------------------------
      // ensure local spell exists
      //--------------------------
      this.local_spell = this.local_spell || {}

      // //-----------------------------------------------
      // // mag counter: validate duplicates for existing
      // //-----------------------------------------------
      if (ref === 'mag') {
        this.local_spell.mag = count
        this.revalidateDuplicateSpellMag()
      }

      // NO UPDATE EMITS AS NO REAL-TIME UPDATES (ONLY ADD)
    },

    //========================================================
    // on spell type select
    //----------------------
    onSpellTypeSelect(type) {
      console.log('onSpellTypeSelect type = ', type)

      // reset any previously selected spell so that
      // details and item selection stay hidden until a new spell is chosen
      this.local_spell.spell = null
      this.local_spell.name = ''
      this.local_spell.mag = 0
      this.local_spell.progressive = false

      // where new rune spell - need to find existing (active) character rune first to link to spell
      if (type === 'Rune') {
        if (!this.character.runes || this.character.runes.length == 0) {
          this.errors.push(window.bbUiErrorMsg('No active runes available'))
          this.errorFields.push('type')
        } else {
          this.getDropDownCharacterRuneValues()
        }
      } else {
        // spell list not constrained by character runes - get all spells of this type + character items for optional linking
        this.local_spell.character_rune = null
        this.getDropDownSpellValues()
        this.getDropDownCharacterItemValues()
      }
    },

    //========================================================
    // on spell select
    //-----------------
    onSpellSelect(spellId) {
      console.log('onSpellSelect spellId = ', spellId)
      //---------------------------
      // ensure local spell exists
      //---------------------------
      this.local_spell = this.local_spell || {}

      this.errors = []
      this.errorFields = []

      if (!spellId) {
        this.local_spell.name = ''
        return
      }

      // lookup the selected static spell from the local dropdown list
      const spells = this.spells || []
      const spell = spells.find((s) => s.id === spellId)

      if (!spell) {
        console.warn('selected spell not found in local spells list:', spellId)
        return
      }

      //===================================
      // A. STATIC VALUES
      //===================================
      //-------------------------------------
      // got static spell - set local values
      //-------------------------------------
      this.save_spell_id = spell.id

      this.local_spell.name = spell.name ?? ''
      this.local_spell.type = spell.type ?? ''
      this.local_spell.mag = spell.mag ?? ''
      this.local_spell.casting_time = spell.casting_time ?? ''
      this.local_spell.duration = spell.duration ?? ''
      this.local_spell.resist = spell.resist ?? ''
      this.local_spell.range = spell.range ?? ''
      this.local_spell.area = spell.area ?? ''
      this.local_spell.progressive = spell.progressive ?? ''
      this.local_spell.triggered = spell.triggered ?? ''
      this.local_spell.description = spell.description ?? ''
      this.local_spell.short_description = spell.short_description ?? ''
      this.local_spell.cult1 = spell.cult1 ?? ''
      this.local_spell.cult2 = spell.cult2 ?? ''

      // set casting rune name for spell source text (e.g. tablespells)
      this.local_spell.casting_rune_name = spell.casting_rune_name ?? ''

      this.revalidateDuplicateSpellMag()
    },

    //========================================================
    // on rune select
    //----------------
    onRuneSelect(characterRuneId) {
      console.log('onRuneSelect characterRuneId = ', characterRuneId)

      this.errors = []
      this.errorFields = []

      // always clear dependent spell/details when rune changes so
      // mag and details don't remain for the wrong rune
      this.local_spell.character_rune = characterRuneId || null
      this.local_spell.spell = null
      this.local_spell.name = ''
      this.local_spell.item_name = ''
      this.local_spell.casting_rune_name = ''
      this.local_spell.mag = 0
      this.local_spell.progressive = false
      this.save_spell_id = null
      this.spells = []

      // where rune selection is cleared, stop here
      if (!characterRuneId) {
        return
      }

      // look up the CharacterRune so we can derive the underlying static rune
      const runes =
        this.runes && this.runes.length > 0
          ? this.runes
          : this.character.runes || []
      const characterRune = runes.find((r) => r.id === characterRuneId)

      console.log('onRuneSelect characterRune = ', characterRune)

      if (!characterRune) {
        console.warn('Character rune not found for id:', characterRuneId)
        return
      }

      const runeName = characterRune.name || ''
      if (!runeName && this.getCharacterRuneStaticId(characterRune) == null) {
        console.warn('Character rune missing name for id:', characterRuneId)
        return
      }

      // persist selected character_rune on local spell (for new-spell payloads)
      this.local_spell.character_rune = characterRuneId

      // set item name for spell source text (e.g. tablespells)
      this.local_spell.item_name = characterRune.item_name || ''

      // go get spells for this rune selection
      this.getDropDownSpellSpecificRuneValues(characterRune)
    },

    //========================================================
    // on item select
    //----------------
    onItemSelect(characterItemId) {
      console.log('onItemSelect characterItemId = ', characterItemId)

      this.errors = []
      this.errorFields = []

      const items =
        this.items && this.items.length > 0
          ? this.items
          : this.character.items || []
      const characterItem = items.find((i) => i.id === characterItemId)

      if (!characterItem) {
        console.error('Character item not found for id:', characterItemId)
        return
      }

      console.log('onItemSelect characterItem = ', characterItem)

      // persist selected character_item on local spell (for new-spell payloads)
      this.local_spell.character_item = characterItemId

      // set source name for spell source text (e.g. tablespells)
      this.local_spell.source_name =
        characterItem.display_name || characterItem.name || ''

      console.log(
        'onItemSelect this.local_spell.source_name = ',
        this.local_spell.source_name,
      )

      // NB: NO EMIT AS NO REAL-TIME UPDATES (ONLY ADD)
    },

    //========================================================
    // get dropdown spell values
    //---------------------------
    getDropDownSpellValues() {
      console.log(
        'getDropDownSpellValues for spell_type = ',
        this.activeSpell.type,
      )
      //-------------------
      // get static spells
      //-------------------
      const getSpellsByType =
        this.$store.getters['characters/getStaticSpellsByType']
      const spells =
        typeof getSpellsByType === 'function'
          ? getSpellsByType(this.activeSpell.type)
          : []
      // exclude fixed-mag (non-progressive) spells that already exist for this character
      this.spells = this.filterAvailableStaticSpells(spells)
    },

    //========================================================
    // get dropdown character rune values
    //------------------------------------
    getDropDownCharacterRuneValues() {
      console.log('getDropDownCharacterRuneValues')
      //------------------------------
      // get character runes (active)
      //------------------------------
      this.runes = this.character.runes.filter((r) => r.active == true)
    },

    //========================================================
    // get dropdown character item values
    //------------------------------------
    getDropDownCharacterItemValues() {
      console.log('getDropDownCharacterItemValues')
      //---------------------
      // get character items
      //---------------------
      this.items = this.character.items
      console.log(
        'getDropDownCharacterItemValues character items = ',
        this.items,
      )
    },

    //========================================================
    // get dropdown spell specific rune values
    //-----------------------------------------
    getDropDownSpellSpecificRuneValues(characterRune) {
      console.log(
        'getDropDownSpellSpecificRuneValues characterRune = ',
        characterRune,
      )
      //------------------------------
      // get spells for selected rune,
      // then require any secondary rune
      // to also be active on character
      //------------------------------
      const getSpellsByType =
        this.$store.getters['characters/getStaticSpellsByType']
      const allRuneSpells =
        typeof getSpellsByType === 'function' ? getSpellsByType('Rune') : []
      const spells = this.filterRuneSpellsForCharacterRune(
        allRuneSpells,
        characterRune,
      )
      // exclude fixed-mag (non-progressive) spells that already exist for this character
      this.spells = this.filterAvailableStaticSpells(spells)
    },

    //=====================================================================
    // confirm deletion
    //------------------
    async confirmDeletion() {
      console.log('confirmDeletion for spell = ', this.activeSpell)

      // customise title/message where deleting a sorcery spell
      // (casting skill will also be removed, including for item-linked spells)
      let title = 'Delete Character Spell'
      let message = 'Confirm Deletion'
      const spellType = this.activeSpell?.type || this.spell_type || ''
      const isItemLinked = !!(
        this.activeSpell && this.activeSpell.character_item
      )

      if (spellType === 'Sorcery') {
        if (isItemLinked) {
          title = 'Delete Item Spell & Casting Skill'
        } else {
          title = 'Delete Character Spell & Casting Skill'
        }
        message = 'This will also delete the linked casting skill.'
      }

      const ok = await this.$refs.confirmDialog.show({
        title: title,
        message: message,
        subject: this.activeSpell.name,
        okButton: 'Delete',
        cancelButton: 'Cancel',
      })
      if (ok) {
        this.$emit('delete-character-spell', this.activeSpell)
        this.closeSidePanel()
      } else {
        this.delete = false
      }
    },

    //========================================================
    // on submit
    //-----------
    onSubmit() {
      if (this.submitCommitted) {
        return
      }

      console.log('---------------------------------')
      console.log('onSubmit')
      this.errors = []
      this.errorFields = []
      //-------------------------------------------------
      // validation: prevent duplicate spell for same mag
      //-------------------------------------------------
      this.revalidateDuplicateSpellMag('mag')

      //-----------------------
      // continue if no errors
      //-----------------------
      if (!this.hasErrors) {
        this.submitCommitted = true

        //-----------------------
        // where adding new spell
        //-----------------------
        if (this.isNewSpell) {
          console.log('onSubmit....')
          //-----------------
          // get spell fields
          //-----------------
          // general fields
          const character = this.character.id ? this.character.id : null
          const character_item = this.activeSpell?.character_item ?? null // optional for sorcery/divine spells
          const character_rune = this.activeSpell?.character_rune ?? null // for character rune-linked spells
          const name = this.activeSpell?.name ?? '' // debug
          const type = this.activeSpell?.type ?? '' // api decisioning
          const mag = this.activeSpell?.mag ?? 0

          // for character rune linked to item (supports source text in tablespells)
          const source_name = this.activeSpell?.source_name ?? null

          // for sorcery/diving spells linked to item (supports source text in tablespells)
          const casting_rune_name = this.activeSpell?.casting_rune_name ?? null
          const item_name = this.activeSpell?.item_name ?? null
          //-----------------
          // set add payload
          //-----------------
          const add_parms = {
            name: name,
            source_name: source_name,
            casting_rune_name: casting_rune_name,
            item_name: item_name,
            payload: {
              id: createTempId(),
              spell: this.save_spell_id,
              character_item: character_item,
              character_rune: character_rune,
              character: character,
              type: type,
              name: name,
              mag: mag,
            },
          }
          //----------------
          // emit add event
          //----------------
          console.log('emitting add-character-spell with parms = ', add_parms)
          this.$emit('add-character-spell', add_parms)
        } else {
          if (this.original_spell.mag !== this.activeSpell?.mag) {
            this.emitUpdate('mag', this.activeSpell?.mag ?? 0)
          }

          if (
            this.original_spell.character_item !==
            (this.activeSpell?.character_item ?? null)
          ) {
            this.emitUpdate(
              'character_item',
              this.activeSpell?.character_item ?? null,
            )
          }

          if (
            this.original_spell.character_rune !==
            (this.activeSpell?.character_rune ?? null)
          ) {
            this.emitUpdate(
              'character_rune',
              this.activeSpell?.character_rune ?? null,
            )
          }
        }
      }
      //----------------------------------
      // where no errors close side panel
      //----------------------------------
      if (!this.hasErrors) this.closeSidePanel()
    },

    //========================================================
    // clear specific input error
    //----------------------------
    clearInputError(field) {
      if (this.errorFields.length > 0) {
        const index = this.errorFields.indexOf(field)
        if (index != -1) {
          this.errors.splice(index, 1)
          this.errorFields.splice(index, 1)
        }
      }
    },

    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      const name = this.activeSpell?.name ?? this.local_spell.name ?? ''
      const id = this.getSpellRowId(this.activeSpell)

      this.$emit('update-character-spell', {
        type: 'spells',
        key,
        value,
        name,
        id,
      })
    },

    //========================================================
    // manage cancel
    //---------------
    manageCancel() {
      this.submitCommitted = false

      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },

    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.$emit('close-spell-panel')
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    // console.log('>>> spell_type = ', this.spell_type)
    //----------------
    // existing spell
    //----------------
    if (this.spell) {
      console.log('EXISTING SPELL...')
      //----------------------------------------------------------
      // save editable fields snapshot for isChanged (and cancel)
      //----------------------------------------------------------
      this.original_spell = {
        mag: this.spell.mag ?? 0,
        character_item: this.spell.character_item ?? null,
        character_rune: this.spell.character_rune ?? null,
      }
      //-------------------------------------------------------
      // copy spell to local spell for v-model (fields changing)
      //-------------------------------------------------------
      this.local_spell = copyObjectNoRef(this.spell) || {}
      console.log('this.local_spell = ', this.local_spell)
      //-------------------------------------------------------
      // load items where sorcery/divine spell (optional link)
      //-------------------------------------------------------
      if (this.spell_type !== 'Rune') {
        this.getDropDownCharacterItemValues()
      }
    } else {
      //-----------
      // new spell
      //-----------
      this.local_spell = setSpellValues()
      console.log('NEW SPELL...')

      // where a specific spell_type is passed in (Rune, Sorcery, Divine),
      // pre-set the local spell type and load the appropriate dropdown data
      if (this.spell_type && this.spell_type !== 'All') {
        this.local_spell.type = this.spell_type
        this.onSpellTypeSelect(this.spell_type)
      }
    }
  },
}
</script>
